import { useMemo } from 'react';
import type { AgentType } from '@/lib/types';
import type { AgentModelEndpoint } from '@reflex/shared';

interface EndpointSupport {
  isEndpointAvailable: (agentType: AgentType, endpointId: string) => boolean;
  getDefaultEndpoint: (agentType: AgentType) => string | null;
}

export interface EffectiveModelEndpointResult {
  /** The resolved endpoint ID to use for the launch. */
  effectiveModelEndpointRouteId: string | null;
  /** True when no endpoint is available for the selected model. */
  hasNoAvailableEndpoint: boolean;
}

export interface UseEffectiveModelEndpointOptions {
  isMatrixDriven: boolean;
  modelEndpointId: string | undefined | null;
  agentType: AgentType;
  model: string;
  allEndpoints: AgentModelEndpoint[];
  availableEndpoints: AgentModelEndpoint[];
  modelSupport: EndpointSupport;
}

/**
 * Resolves the effective model endpoint to use for an agent launch.
 *
 * Resolution order:
 * 1. User-selected endpoint (if valid and available)
 * 2. Curated default endpoint (if available)
 * 3. First available endpoint
 *
 * This hook encapsulates the endpoint validation logic that was previously
 * inline in the LaunchAgentDialog.
 */
export function useEffectiveModelEndpoint({
  isMatrixDriven,
  modelEndpointId,
  agentType,
  model,
  allEndpoints,
  availableEndpoints,
  modelSupport,
}: UseEffectiveModelEndpointOptions): EffectiveModelEndpointResult {
  const effectiveModelEndpointRouteId = useMemo(() => {
    if (!isMatrixDriven) return null;

    // Check if user-selected endpoint is valid and available
    if (
      modelEndpointId &&
      allEndpoints.some((e) => e.id === modelEndpointId) &&
      modelSupport.isEndpointAvailable(agentType, modelEndpointId)
    ) {
      return modelEndpointId;
    }

    // Prefer curated default if available
    const defaultEndpoint = modelSupport.getDefaultEndpoint(agentType);
    if (
      defaultEndpoint &&
      availableEndpoints.some((e) => e.id === defaultEndpoint) &&
      modelSupport.isEndpointAvailable(agentType, defaultEndpoint)
    ) {
      return defaultEndpoint;
    }

    // Fall back to first available endpoint
    return availableEndpoints[0]?.id ?? null;
  }, [isMatrixDriven, modelEndpointId, allEndpoints, availableEndpoints, modelSupport, agentType]);

  const hasNoAvailableEndpoint = isMatrixDriven && Boolean(model) && !effectiveModelEndpointRouteId;

  return {
    effectiveModelEndpointRouteId,
    hasNoAvailableEndpoint,
  };
}
